import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface TutorialStep {
  target: string;
  titleKey: keyof typeof import('../i18n/translations').translations.sv;
  descKey: keyof typeof import('../i18n/translations').translations.sv;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tutorialStepKeys: TutorialStep[] = [
  {
    target: '#map',
    titleKey: 'tutorialMapTitle',
    descKey: 'tutorialMapDesc',
    position: 'bottom'
  },
  {
    target: '#date-input',
    titleKey: 'tutorialDateTitle',
    descKey: 'tutorialDateDesc',
    position: 'top'
  },
  {
    target: '#current-location-btn',
    titleKey: 'tutorialLocationTitle',
    descKey: 'tutorialLocationDesc',
    position: 'bottom'
  },
  {
    target: '#weather-info',
    titleKey: 'tutorialWeatherTitle',
    descKey: 'tutorialWeatherDesc',
    position: 'top'
  },
  {
    target: '#hourly-weather',
    titleKey: 'tutorialHourlyTitle',
    descKey: 'tutorialHourlyDesc',
    position: 'top'
  },
  {
    target: '#weather-history',
    titleKey: 'tutorialHistoryTitle',
    descKey: 'tutorialHistoryDesc',
    position: 'top'
  },
  {
    target: '#fishing-forecast-section',
    titleKey: 'tutorialFishingTitle',
    descKey: 'tutorialFishingDesc',
    position: 'top'
  },
  {
    target: '#ai-recommendation-section',
    titleKey: 'tutorialAITitle',
    descKey: 'tutorialAIDesc',
    position: 'top'
  },
  {
    target: '#wind-simulator-section',
    titleKey: 'tutorialWindTitle',
    descKey: 'tutorialWindDesc',
    position: 'top'
  },
  {
    target: '#favorites-container-wrapper',
    titleKey: 'tutorialFavoritesTitle',
    descKey: 'tutorialFavoritesDesc',
    position: 'top'
  },
  {
    target: '.footer-nav',
    titleKey: 'tutorialNavigationTitle',
    descKey: 'tutorialNavigationDesc',
    position: 'top'
  }
];

interface TutorialProps {
  onComplete: () => void;
  onStepChange?: (step: number) => void;
}

const Tutorial: React.FC<TutorialProps> = ({ onComplete, onStepChange }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    // Prevent body scroll when tutorial is active
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const updatePositions = () => {
      const step = tutorialStepKeys[currentStep];
      const element = document.querySelector(step.target);
      
      if (!element) {
        // Retry after a short delay if element not found
        setTimeout(updatePositions, 100);
        return;
      }

      // Scroll element into view smoothly
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Wait a bit for scroll to complete before updating positions
      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        const padding = 10;

        // Highlight position (using viewport coordinates, not scroll)
        setHighlightPosition({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2
        });

        // Tooltip position (relative to viewport)
        let top = 0;
        let left = 0;
        const tooltipWidth = 260;
        const tooltipHeight = 160; // Approximate height
        const margin = 20;
        const safeMargin = 20;

        // Calculate initial position based on step preference
        switch (step.position) {
          case 'bottom':
            top = rect.bottom + margin;
            left = rect.left + rect.width / 2;
            break;
          case 'top':
            top = rect.top - tooltipHeight - margin;
            left = rect.left + rect.width / 2;
            break;
          case 'right':
            top = rect.top + rect.height / 2;
            left = rect.right + margin;
            break;
          case 'left':
            top = rect.top + rect.height / 2;
            left = rect.left - margin;
            break;
        }

        // Keep tooltip within viewport bounds
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Ensure tooltip fits horizontally (center it)
        left = Math.max(safeMargin + tooltipWidth / 2, Math.min(left, viewportWidth - safeMargin - tooltipWidth / 2));

        // Ensure tooltip fits vertically
        top = Math.max(safeMargin, Math.min(top, viewportHeight - tooltipHeight - safeMargin));

        setTooltipPosition({ top, left });
      }, 500);
    };

    // Wait for DOM to be ready
    const timer = setTimeout(updatePositions, 100);
    
    window.addEventListener('resize', updatePositions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePositions);
    };
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < tutorialStepKeys.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (onStepChange) {
        onStepChange(nextStep);
      }
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    localStorage.setItem('weatherAppOnboarding', 'true');
    onComplete();
  };

  const step = tutorialStepKeys[currentStep];
  const title = t[step.titleKey];
  const description = t[step.descKey];

  return (
    <div id="tutorialOverlay" className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Dark backdrop with cutout - covers entire viewport */}
      <div 
        id="tutorialBackdrop" 
        className="fixed inset-0 transition-all duration-500"
        style={{
          background: `radial-gradient(
            circle at ${highlightPosition.left + highlightPosition.width / 2}px ${highlightPosition.top + highlightPosition.height / 2}px,
            transparent ${Math.max(highlightPosition.width, highlightPosition.height) / 2 + 20}px,
            rgba(0, 0, 0, 0.75) ${Math.max(highlightPosition.width, highlightPosition.height) / 2 + 40}px
          )`,
          zIndex: 9999,
          pointerEvents: 'none' // Let clicks through
        }}
      />

      {/* Highlight box - fixed position relative to viewport */}
      <div
        className="fixed transition-all duration-300 pointer-events-none"
        style={{
          top: `${highlightPosition.top}px`,
          left: `${highlightPosition.left}px`,
          width: `${highlightPosition.width}px`,
          height: `${highlightPosition.height}px`,
          border: '3px solid rgba(59, 130, 246, 0.9)',
          borderRadius: '12px',
          boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.2), 0 0 40px rgba(59, 130, 246, 0.6), 0 0 80px rgba(59, 130, 246, 0.4)',
          zIndex: 10000,
          animation: 'tutorialPulse 2s ease-in-out infinite'
        }}
      />

      {/* Tooltip - fixed position relative to viewport */}
      <div
        id="tutorialTooltip"
        className="fixed bg-white text-gray-800 p-4 rounded-lg shadow-2xl transition-all duration-300 pointer-events-auto"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: 'translate(-50%, 0)',
          zIndex: 10001,
          maxWidth: '260px',
          width: 'calc(100vw - 40px)',
          maxHeight: '50vh',
          overflowY: 'auto'
        }}
      >
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-base font-bold">{title}</h3>
            <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
              {currentStep + 1}/{tutorialStepKeys.length}
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleSkip}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {t.tutorialSkip}
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {currentStep < tutorialStepKeys.length - 1 ? t.tutorialNext : t.tutorialComplete}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tutorialPulse {
          0%, 100% {
            box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.9), 0 0 40px rgba(59, 130, 246, 0.8), 0 0 80px rgba(59, 130, 246, 0.5);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 1), 0 0 60px rgba(59, 130, 246, 0.9), 0 0 120px rgba(59, 130, 246, 0.6);
            transform: scale(1.01);
          }
        }
      `}</style>
    </div>
  );
};

export default Tutorial;
